import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Case } from 'src/app/core/models/case';
import { CaseService } from 'src/app/core/services/case.service';
import { ClaimsService } from 'src/app/core/services/claims.service';

@Component({
  selector: 'app-cases',
  templateUrl: './cases.component.html',
  styleUrls: ['./cases.component.scss'],
})
export class CasesComponent implements OnInit, OnDestroy {
  publicCases?: Case[];

  selectedIndex = 0;
  showOtherCases = false;
  loaded = false;

  private paramsSub?: Subscription;

  constructor(
    private cs: CaseService,
    private claimsService: ClaimsService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  async ngOnInit() {
    const cases = await this.cs.getAll(2000, 0) as Case[];

    let enrolledCaseIds = new Set<string>();
    let claimsLoadFailed = false;
    try {
      const myClaims = await this.claimsService.getMyClaimsWithCase(2000, 0);
      enrolledCaseIds = new Set(myClaims.map(c => c.case_id));
    } catch (e) {
      console.warn('[CASES] Could not load user claims, hiding available cases as safe default', e);
      claimsLoadFailed = true;
    }

    // Codes dos casos em que o cliente já tem claim — base da visibilidade direcionada
    const enrolledCaseCodes = new Set(
      cases.filter(c => enrolledCaseIds.has(c.id)).map(c => c.code)
    );

    this.publicCases = claimsLoadFailed ? [] : cases.filter(
      c => !enrolledCaseIds.has(c.id) && this.isVisibleToClient(c, enrolledCaseCodes)
    );
    this.showOtherCases = this.publicCases.length > 0;

    // Subscription ativa para reagir ao sidebar (reutiliza componente sem ngOnInit)
    this.paramsSub = this.route.queryParams.subscribe(params => {
      const isOpen = params['open'] === 'true';
      this.selectedIndex = isOpen ? 1 : 0;
    });

    this.loaded = true;
  }

  onTabChange(index: number) {
    if (!this.loaded) return;

    const shouldBeOpen = index === 1;
    const currentOpen = this.route.snapshot.queryParams['open'] === 'true';
    if (shouldBeOpen === currentOpen) return;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { open: shouldBeOpen },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Um caso aparece na lista de "outros casos" apenas se houver visibilidade
   * direcionada habilitada e o cliente tiver claim em algum dos casos de
   * origem configurados (configuration.client_visibility) — `public` não
   * concede visibilidade aqui (CPK-4788).
   */
  private isVisibleToClient(c: Case, enrolledCaseCodes: Set<string>): boolean {
    const visibility = (c.configuration as any)?.client_visibility;
    if (!visibility?.enabled) return false;

    const codes: string[] = Array.isArray(visibility.visible_to_case_codes)
      ? visibility.visible_to_case_codes
      : [];

    return codes.some(code => enrolledCaseCodes.has(code));
  }

  ngOnDestroy() {
    this.paramsSub?.unsubscribe();
  }
}
